import { isEmpty, isNil } from 'lodash';
import { CSSProperties, ReactNode, useCallback, useMemo, useState } from 'react';
import { MBTextInput } from '@shared/components/textInput';
import { EmailField } from '@shared/data/entity/fields/EmailField';
import { useComponentFocus } from '@shared/scenes/cards/form/components/ComponentFocus';
import { getComponentPlaceholder } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { EditFieldContainer } from './EditFieldContainer';

interface Props {
  field: EmailField | null;
  component: API.EmailComponentFragment | API.RelatedCardLookupComponent;
  updateField?: (componentId: string, field: EmailField | null) => void;
  error?: string;
  allowClear?: boolean;
  showIcon?: boolean;
  label?: string;
  showLabel?: boolean;
  reserveIconSpace?: boolean;
  compactTop?: boolean;
  compactBottom?: boolean;
  subfooter?: ReactNode;
  isGrayScale?: boolean;
  isItalic?: boolean;
  style?: CSSProperties;
}

export function EmailEditField({
  field,
  component,
  updateField,
  error,
  allowClear,
  showIcon,
  label,
  showLabel = true,
  reserveIconSpace,
  compactTop,
  compactBottom,
  subfooter,
  isGrayScale,
  isItalic,
  style,
}: Props) {
  // `draft` is null until the user edits, so the input derives straight from `field` and
  // picks up async-loaded data with no effect or in-render setState. Once they type, `draft`
  // takes over and lets them enter in-progress text (trailing commas/spaces) that wouldn't
  // survive a round-trip through `EmailField`.
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? field?.emails.join(', ') ?? '';
  const [isFocused, onFocus, onBlur] = useBoolean(false);
  const { setComponentFocused, setComponentBlurred } = useComponentFocus();

  // Derive synchronously from `value` so it stays in lockstep with the input. When this
  // trailed `value` via a useEffect, the 0→1 character transition left `hasInput` false
  // for one frame, which flashed the error through the `!hasInput` gate below.
  const hasInput = !isEmpty(getEmailsFromInput(value));
  const showLocalError = !isFocused;
  // Surface email *formatting* errors on blur, independent of the form-level `error`
  // prop (which only includes local validation after a submit attempt). Required-field
  // errors still come solely from `error` and remain submit-gated.
  const formatError = useMemo(() => {
    const emails = getEmailsFromInput(value);
    if (isEmpty(emails)) {
      return undefined;
    }
    return new EmailField(emails).validate() ?? undefined;
  }, [value]);
  const displayError = error ?? formatError;
  const showError = (!hasInput || showLocalError) && !isEmpty(displayError);

  const handleFocus = useCallback(() => {
    onFocus();
    setComponentFocused();
  }, [setComponentFocused, onFocus]);

  const handleBlur = useCallback(() => {
    onBlur();
    setComponentBlurred();
  }, [setComponentBlurred, onBlur]);

  const handleChangeText = useCallback(
    (text: string) => {
      setDraft(text);
      const emails = getEmailsFromInput(text);
      updateField?.(component.id, !isEmpty(emails) ? new EmailField(emails) : null);
    },
    [updateField, component.id],
  );

  const onClear = useCallback(() => {
    setDraft('');
    updateField?.(component.id, null);
  }, [updateField, component.id]);

  const hasValue = !isEmpty(value);
  const isReadOnly = component.readonly || isNil(updateField);
  const showClearButton = hasValue && !isReadOnly && allowClear;

  return (
    <EditFieldContainer
      component={component}
      showIcon={showIcon}
      label={label}
      showLabel={showLabel}
      reserveIconSpace={reserveIconSpace}
      showClearButton={showClearButton}
      onClear={onClear}
      error={showError ? displayError : undefined}
      compactTop={compactTop}
      compactBottom={compactBottom}
      subfooter={subfooter}
      isGrayScale={isGrayScale}
      isItalic={isItalic}
      style={style}
      printContent={field?.emails.join(', ') || undefined}
    >
      <MBTextInput
        noBorder
        noPadding
        inputMode="email"
        textContentType="emailAddress"
        keyboardType="email-address"
        value={value}
        onChangeText={handleChangeText}
        placeholder={getComponentPlaceholder(component)}
        onBlur={handleBlur}
        onFocus={handleFocus}
        editable={!isReadOnly}
        style={{ paddingVertical: isEmpty(value) ? grid(1.25) : undefined }}
      />
    </EditFieldContainer>
  );
}

function getEmailsFromInput(input: string) {
  return input
    .replace(/( )?,( )?/g, ',')
    .trim()
    .split(',')
    .filter(Boolean);
}
